import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Search, RefreshCw, Send, X, Check, AlertCircle, Loader2,
  FileText, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { documentDeliveryService } from '../../../../services/documentDeliveryService';
import type {
  DocumentDeliveryLog,
  DocumentDeliverySearchParams,
  EsentralResult,
} from '../../../../types/documentDelivery';

// ==================== STATUS CLASSNAMES ====================

const statusClassNames: Record<string, string> = {
  Queued: 'bg-yellow-100 text-yellow-800',
  Success: 'bg-green-100 text-green-800',
  Failed: 'bg-red-100 text-red-800',
};

// ==================== COMPONENT ====================

export const DocumentDeliveryHistory: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // ==================== LABELS (translated) ====================

  const documentTypeLabels = useMemo<Record<string, string>>(() => ({
    GoodsReceipt: t('manager.modules.adminDocDelivery.documentTypes.goodsReceipt', {}, 'Varemottak'),
    PickingList: t('manager.modules.adminDocDelivery.documentTypes.pickingList', {}, 'Plukk'),
    StockCounting: t('manager.modules.adminDocDelivery.documentTypes.stockCounting', {}, 'Telling'),
    StockTransfer: t('manager.modules.adminDocDelivery.documentTypes.stockTransfer', {}, 'Lokasjonsflytt'),
    StockCorrection: t('manager.modules.adminDocDelivery.documentTypes.stockCorrection', {}, 'Korreksjon'),
  }), [t]);

  const documentTypeOptions = useMemo(() => [
    { value: '', label: t('manager.modules.adminDocDelivery.filters.allTypes', {}, 'Alle typer') },
    { value: 'GoodsReceipt', label: documentTypeLabels.GoodsReceipt },
    { value: 'PickingList', label: documentTypeLabels.PickingList },
    { value: 'StockCounting', label: documentTypeLabels.StockCounting },
    { value: 'StockTransfer', label: documentTypeLabels.StockTransfer },
    { value: 'StockCorrection', label: documentTypeLabels.StockCorrection },
  ], [t, documentTypeLabels]);

  const statusLabelText = useMemo<Record<string, string>>(() => ({
    Queued: t('manager.modules.adminDocDelivery.statuses.queued', {}, 'I kø'),
    Success: t('manager.modules.adminDocDelivery.statuses.success', {}, 'OK'),
    Failed: t('manager.modules.adminDocDelivery.statuses.failed', {}, 'Feilet'),
  }), [t]);

  const statusOptions = useMemo(() => [
    { value: '', label: t('manager.modules.adminDocDelivery.filters.allStatuses', {}, 'Alle statuser') },
    { value: 'Queued', label: statusLabelText.Queued },
    { value: 'Success', label: statusLabelText.Success },
    { value: 'Failed', label: statusLabelText.Failed },
  ], [t, statusLabelText]);

  // Search state
  const [filters, setFilters] = useState<DocumentDeliverySearchParams>({
    documentType: null,
    status: null,
    fromDate: null,
    toDate: null,
  });

  // Results state
  const [logs, setLogs] = useState<DocumentDeliveryLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  // Resend dialog state
  const [selectedLog, setSelectedLog] = useState<DocumentDeliveryLog | null>(null);
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState<EsentralResult | null>(null);

  // Toast state
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);

  // ==================== SEARCH ====================

  const fetchPage = useCallback(async (page: number, size: number, searchFilters?: typeof filters) => {
    if (!companyNo) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    const activeFilters = searchFilters ?? filters;
    const offset = (page - 1) * size;

    try {
      const [items, count] = await Promise.all([
        documentDeliveryService.search(companyNo, {
          ...activeFilters,
          limit: size,
          offset,
        }),
        documentDeliveryService.searchCount(companyNo, activeFilters),
      ]);
      setLogs(items);
      setTotalCount(count);
      setCurrentPage(page);
    } catch (err) {
      console.error('Search failed:', err);
      setError(err instanceof Error ? err.message : t('manager.modules.adminDocDelivery.history.searchFailed', {}, 'Søk feilet'));
    } finally {
      setLoading(false);
    }
  }, [companyNo, filters, t]);

  const handleSearch = useCallback(async () => {
    setCurrentPage(1);
    await fetchPage(1, pageSize);
  }, [fetchPage, pageSize]);

  const handleLoadRecent = useCallback(async () => {
    const defaultFilters: DocumentDeliverySearchParams = {
      documentType: null,
      status: null,
      fromDate: null,
      toDate: null,
    };
    setFilters(defaultFilters);
    setCurrentPage(1);
    await fetchPage(1, pageSize, defaultFilters);
  }, [fetchPage, pageSize]);

  // Auto-search on mount
  useEffect(() => {
    fetchPage(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handlePageChange = useCallback((page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchPage(page, pageSize);
  }, [fetchPage, pageSize, totalPages, currentPage]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchPage(1, newSize);
  }, [fetchPage]);

  // ==================== RESEND ====================

  const openResendDialog = (log: DocumentDeliveryLog) => {
    setSelectedLog(log);
    setResendResult(null);
  };

  const closeResendDialog = () => {
    setSelectedLog(null);
    setResendResult(null);
  };

  const showToast = (message: string, success: boolean) => {
    setToast({ message, success });
    setTimeout(() => setToast(null), 4000);
  };

  const handleResend = async () => {
    if (!companyNo || !selectedLog) return;

    setResending(true);
    setResendResult(null);

    try {
      const result = await documentDeliveryService.resend(companyNo, selectedLog.logID);
      setResendResult(result);

      if (result.success) {
        showToast(t('manager.modules.adminDocDelivery.resend.toastSuccess', {}, 'Dokument sendt på nytt'), true);
        setTimeout(() => {
          closeResendDialog();
          fetchPage(currentPage, pageSize);
        }, 1200);
      } else {
        showToast(result.errorMessage || t('manager.modules.adminDocDelivery.resend.failed', {}, 'Sending feilet'), false);
      }
    } catch (err) {
      console.error('Resend failed:', err);
      const message = err instanceof Error ? err.message : t('manager.modules.adminDocDelivery.resend.failed', {}, 'Sending feilet');
      setResendResult({ success: false, errorMessage: message, httpStatusCode: null });
      showToast(message, false);
    } finally {
      setResending(false);
    }
  };

  // ==================== HELPERS ====================

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('nb-NO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ==================== RENDER ====================

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[60] flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            toast.success
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {toast.success ? (
            <Check className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminDocDelivery.history.title', {}, 'eSENTRAL leveringshistorikk')}</h2>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              title={t('manager.modules.adminDocDelivery.history.perPageTitle', {}, 'Antall per side')}
            >
              <option value={50}>{t('manager.modules.adminDocDelivery.history.perPage', { '0': 50 }, '50 per side')}</option>
              <option value={100}>{t('manager.modules.adminDocDelivery.history.perPage', { '0': 100 }, '100 per side')}</option>
              <option value={200}>{t('manager.modules.adminDocDelivery.history.perPage', { '0': 200 }, '200 per side')}</option>
              <option value={500}>{t('manager.modules.adminDocDelivery.history.perPage', { '0': 500 }, '500 per side')}</option>
            </select>
            <button
              onClick={handleLoadRecent}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              {t('manager.modules.adminDocDelivery.history.recent', {}, 'Siste')}
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('manager.modules.adminDocDelivery.filters.documentType', {}, 'Dokumenttype')}</label>
            <select
              value={filters.documentType || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, documentType: e.target.value || null }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            >
              {documentTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.fields.status', {}, 'Status')}</label>
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value || null }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            >
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('manager.modules.adminDocDelivery.filters.fromDate', {}, 'Fra dato')}</label>
            <input
              type="date"
              value={filters.fromDate || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value || null }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('manager.modules.adminDocDelivery.filters.toDate', {}, 'Til dato')}</label>
            <input
              type="date"
              value={filters.toDate || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value || null }))}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <Search className="w-3.5 h-3.5" />
              {t('common.buttons.search', {}, 'Søk')}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Results table */}
      {hasSearched && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              {t('manager.modules.adminDocDelivery.history.searching', {}, 'Søker...')}
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('manager.modules.adminDocDelivery.history.noResults', {}, 'Ingen leveringshistorikk funnet')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('warehouse.general.timestamp', {}, 'Tidspunkt')}</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('common.fields.type', {}, 'Type')}</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('manager.modules.adminDocDelivery.history.columns.fileName', {}, 'Filnavn')}</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('manager.modules.adminDocDelivery.history.columns.reference', {}, 'Referanse')}</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">{t('common.fields.status', {}, 'Status')}</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">{t('manager.modules.adminDocDelivery.history.columns.errorMessage', {}, 'Feilmelding')}</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">{t('manager.modules.adminDocDelivery.history.columns.action', {}, 'Handling')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log) => {
                    const status = {
                      text: statusLabelText[log.status] || log.status,
                      className: statusClassNames[log.status] || 'bg-gray-100 text-gray-800',
                    };
                    const canResend = log.status === 'Failed';

                    return (
                      <tr key={log.logID} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                          {formatDateTime(log.createdDateTime)}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {documentTypeLabels[log.documentType] || log.documentType || '-'}
                          {log.originalLogID && (
                            <span className="ml-1 text-xs text-blue-600">(#{log.originalLogID})</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700 font-mono text-xs max-w-[200px] truncate" title={log.fileName}>
                          {log.fileName || '-'}
                        </td>
                        <td className="px-3 py-2 text-gray-700 font-mono text-xs">
                          {log.referenceID || '-'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                            {status.text}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-red-600 text-xs max-w-[240px] truncate" title={log.errorMessage || ''}>
                          {log.errorMessage || '-'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {canResend ? (
                            <button
                              onClick={() => openResendDialog(log)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 rounded transition-colors"
                              title={t('manager.modules.adminDocDelivery.resend.button', {}, 'Send på nytt')}
                            >
                              <Send className="w-3 h-3" />
                              {t('manager.modules.adminDocDelivery.resend.button', {}, 'Send på nytt')}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {logs.length > 0 && (
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {(() => {
                  const fromItem = ((currentPage - 1) * pageSize) + 1;
                  const toItem = Math.min(currentPage * pageSize, totalCount);
                  const resultWord = totalCount !== 1
                    ? t('manager.modules.adminDocDelivery.history.resultPlural', {}, 'resultater')
                    : t('manager.modules.adminDocDelivery.history.resultSingular', {}, 'resultat');
                  return t(
                    'manager.modules.adminDocDelivery.history.showingRange',
                    { '0': fromItem, '1': toItem, '2': totalCount, '3': resultWord },
                    `Viser ${fromItem}–${toItem} av ${totalCount} ${resultWord}`
                  );
                })()}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || loading}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('manager.modules.adminDocDelivery.history.firstPage', {}, 'Første side')}
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('manager.modules.adminDocDelivery.history.previousPage', {}, 'Forrige side')}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2 text-xs text-gray-600 font-medium">
                    {t('manager.modules.adminDocDelivery.history.pageOf', { '0': currentPage, '1': totalPages }, `Side ${currentPage} av ${totalPages}`)}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('manager.modules.adminDocDelivery.history.nextPage', {}, 'Neste side')}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages || loading}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('manager.modules.adminDocDelivery.history.lastPage', {}, 'Siste side')}
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== RESEND CONFIRMATION DIALOG ==================== */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                {t('manager.modules.adminDocDelivery.resend.dialogTitle', {}, 'Send dokument på nytt')}
              </h3>
              <button
                onClick={closeResendDialog}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Original delivery info */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manager.modules.adminDocDelivery.resend.logId', {}, 'Logg-ID:')}</span>
                  <span className="font-medium">#{selectedLog.logID}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manager.modules.adminDocDelivery.resend.type', {}, 'Type:')}</span>
                  <span>{documentTypeLabels[selectedLog.documentType] || selectedLog.documentType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manager.modules.adminDocDelivery.resend.fileName', {}, 'Filnavn:')}</span>
                  <span className="font-mono text-xs max-w-[240px] truncate" title={selectedLog.fileName}>
                    {selectedLog.fileName}
                  </span>
                </div>
                {selectedLog.referenceID && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('manager.modules.adminDocDelivery.resend.reference', {}, 'Referanse:')}</span>
                    <span className="font-mono">{selectedLog.referenceID}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('manager.modules.adminDocDelivery.resend.created', {}, 'Opprettet:')}</span>
                  <span>{formatDateTime(selectedLog.createdDateTime)}</span>
                </div>
                {selectedLog.retryCount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('manager.modules.adminDocDelivery.resend.previousAttempts', {}, 'Tidligere forsøk:')}</span>
                    <span>{selectedLog.retryCount}</span>
                  </div>
                )}
              </div>

              {/* Error message from original attempt */}
              {selectedLog.errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                  <span className="font-medium block mb-1">{t('manager.modules.adminDocDelivery.resend.previousError', {}, 'Feilmelding fra forrige forsøk:')}</span>
                  {selectedLog.errorMessage}
                </div>
              )}

              <p className="text-sm text-gray-600">
                {t('manager.modules.adminDocDelivery.resend.confirm', {}, 'Er du sikker på at du vil sende dette dokumentet til eSENTRAL på nytt?')}
              </p>

              {/* Result feedback */}
              {resendResult && (
                <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${
                  resendResult.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {resendResult.success ? (
                    <Check className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  )}
                  {resendResult.success
                    ? t('manager.modules.adminDocDelivery.resend.resultSuccess', {}, 'Dokumentet ble sendt på nytt')
                    : resendResult.errorMessage || t('manager.modules.adminDocDelivery.resend.failed', {}, 'Sending feilet')}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={closeResendDialog}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleResend}
                disabled={resending || resendResult?.success === true}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {t('manager.modules.adminDocDelivery.resend.button', {}, 'Send på nytt')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDeliveryHistory;
